<template>
  <v-card class="mt-4">
    <v-snackbar v-model="snackbar" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
    <v-card-text>
      <v-data-table :search="search" :loading="loading" :headers="headers" :items="items">
        <template v-slot:top>
          <v-row>
            <v-spacer />
            <v-col cols="2">
              <v-text-field v-model="search" label="Search" solo dense prepend-inner-icon="mdi-magnify" />
            </v-col>
            <v-col cols="auto">
              <v-btn color="primary" @click="openAddDialog">Add Tenant</v-btn>
            </v-col>
          </v-row>
        </template>
        <template v-slot:item.primaryColor="{ item }">
          <v-avatar v-if="item.primaryColor" :color="item.primaryColor" size="24" />
          <span v-else class="grey--text text--lighten-1">—</span>
        </template>
        <template v-slot:item.appBarColor="{ item }">
          <v-avatar v-if="item.appBarColor" :color="item.appBarColor" size="24" />
          <span v-else class="grey--text text--lighten-1">—</span>
        </template>
        <template v-slot:item.logo="{ item }">
          <v-avatar v-if="item.logo" tile size="32">
            <v-img :src="item.logo" contain />
          </v-avatar>
          <span v-else class="grey--text text--lighten-1">—</span>
        </template>
        <template v-slot:item.enabled="{ item }">
          <v-icon :color="item.enabled ? 'green' : 'grey'">{{ item.enabled ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
          <v-btn x-small text @click="toggleEnabled(item)">{{ item.enabled ? 'Disable' : 'Enable' }}</v-btn>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn x-small text @click="openEditDialog(item)">Edit</v-btn>
        </template>
      </v-data-table>
      <v-dialog v-model="dialog" max-width="520" persistent scrollable>
        <v-card>
          <v-card-title>{{ isEdit ? 'Edit Tenant' : 'Add Tenant' }}</v-card-title>
          <v-card-text>
            <v-text-field v-if="isEdit" v-model="form.tenantId" label="Tenant ID" outlined dense disabled />
            <v-text-field v-model="form.tenantName" label="Tenant Name" outlined dense />
            <v-text-field v-model="form.botId" label="Bot ID (optional)" outlined dense />
            <v-text-field
              v-model="form.primaryColor"
              label="Primary Color"
              placeholder="#D97706"
              outlined
              dense
              hint="สีหลักของปุ่มและองค์ประกอบ UI"
              persistent-hint
            />
            <v-color-picker
              v-model="form.primaryColor"
              flat
              hide-canvas
              hide-inputs
              show-swatches
              swatches-max-height="80"
              class="mt-2"
            />
            <v-text-field
              v-model="form.appBarColor"
              label="App Bar Color"
              placeholder="#D97706"
              outlined
              dense
              class="mt-4"
              hint="สีแถบด้านบน (ว่าง = ใช้ Primary Color)"
              persistent-hint
            />
            <v-color-picker
              v-model="form.appBarColor"
              flat
              hide-canvas
              hide-inputs
              show-swatches
              swatches-max-height="80"
              class="mt-2"
            />
            <v-text-field
              v-model="form.logo"
              label="Logo URL"
              placeholder="https://..."
              outlined
              dense
              class="mt-4"
              hint="URL รูปโลโก้สำหรับ dashboard"
              persistent-hint
            />
            <v-img
              v-if="form.logo"
              :src="form.logo"
              max-height="120"
              contain
              class="mt-2 rounded"
              @error="logoImageError = true"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey lighten-5" />
                </v-row>
              </template>
            </v-img>
            <v-alert v-if="logoImageError" type="warning" dense class="mt-2" text>
              โหลดรูปไม่สำเร็จ กรุณาตรวจสอบ URL
            </v-alert>
            <v-checkbox v-model="form.enabled" label="Enabled" hide-details class="mt-2" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="dialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="posting" @click="save">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import { getAdminTenants, postAdminTenant, patchAdminTenant } from '@/api/'

export default {
  name: 'AdminTenantTable',
  data() {
    return {
      search: '',
      loading: false,
      posting: false,
      snackbar: false,
      snackbarMessage: '',
      dialog: false,
      isEdit: false,
      logoImageError: false,
      items: [],
      form: {
        tenantId: '',
        tenantName: '',
        botId: '',
        enabled: true,
        primaryColor: '',
        appBarColor: '',
        logo: ''
      },
      headers: [
        { text: 'Tenant ID', value: 'tenantId' },
        { text: 'Tenant Name', value: 'tenantName' },
        { text: 'Primary', value: 'primaryColor', sortable: false },
        { text: 'App Bar', value: 'appBarColor', sortable: false },
        { text: 'Logo', value: 'logo', sortable: false },
        { text: 'Enabled', value: 'enabled' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  watch: {
    'form.logo'() {
      this.logoImageError = false
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      getAdminTenants(
        (res) => {
          this.items = res.data || []
          this.loading = false
        },
        () => {
          this.loading = false
          this.showMessage('Failed to load tenants')
        }
      )
    },
    openAddDialog() {
      this.isEdit = false
      this.logoImageError = false
      this.form = {
        tenantId: '',
        tenantName: '',
        botId: '',
        enabled: true,
        primaryColor: '',
        appBarColor: '',
        logo: ''
      }
      this.dialog = true
    },
    openEditDialog(item) {
      this.isEdit = true
      this.logoImageError = false
      this.form = {
        tenantId: item.tenantId,
        tenantName: item.tenantName || '',
        botId: item.botId || '',
        enabled: item.enabled !== false,
        primaryColor: item.primaryColor || '',
        appBarColor: item.appBarColor || '',
        logo: item.logo || ''
      }
      this.dialog = true
    },
    brandingPayload() {
      const normalize = (color) => {
        if (!color) return undefined
        const match = String(color).trim().match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/)
        if (!match) return undefined
        let hex = match[1]
        if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('')
        else if (hex.length === 8) hex = hex.slice(0, 6)
        return `#${hex}`
      }
      return {
        primaryColor: normalize(this.form.primaryColor),
        appBarColor: normalize(this.form.appBarColor),
        logo: this.form.logo || undefined
      }
    },
    save() {
      this.posting = true
      if (this.isEdit) {
        patchAdminTenant(
          {
            tenantId: this.form.tenantId,
            tenantName: this.form.tenantName,
            botId: this.form.botId,
            enabled: this.form.enabled,
            ...this.brandingPayload()
          },
          () => {
            this.posting = false
            this.dialog = false
            this.showMessage('Tenant updated')
            this.load()
            this.$emit('refresh-tenants')
          },
          (err) => {
            this.posting = false
            this.showMessage(err?.response?.data?.error || 'Failed to update')
          }
        )
      } else {
        postAdminTenant(
          {
            tenantName: this.form.tenantName,
            botId: this.form.botId || undefined,
            enabled: this.form.enabled,
            ...this.brandingPayload()
          },
          () => {
            this.posting = false
            this.dialog = false
            this.showMessage('Tenant created')
            this.load()
            this.$emit('refresh-tenants')
          },
          (err) => {
            this.posting = false
            this.showMessage(err?.response?.data?.error || 'Failed to create')
          }
        )
      }
    },
    toggleEnabled(item) {
      patchAdminTenant(
        { tenantId: item.tenantId, enabled: !item.enabled },
        () => {
          this.showMessage('Updated')
          this.load()
          this.$emit('refresh-tenants')
        },
        (err) => this.showMessage(err?.response?.data?.error || 'Failed')
      )
    },
    showMessage(msg) {
      this.snackbarMessage = msg
      this.snackbar = true
    }
  }
}
</script>
